import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Api, UNAUTHORIZED_EVENT, type Me, type Tenant, type User } from './api.ts';

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** Tenant the user is working with (admins can switch). */
  tenant: Tenant | null;
  setTenantId: (id: string) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reload: () => Promise<void>;
  /** May manage vaults, install tokens and users of the current tenant. */
  canManage: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthState | null>(null);
const TENANT_KEY = 'mv.tenant';

function storedTenant(): string | null {
  try {
    return localStorage.getItem(TENANT_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantIdState] = useState<string | null>(storedTenant());

  const reload = useCallback(async () => {
    try {
      setMe(await Api.me());
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    const onUnauthorized = () => setMe(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [reload]);

  const setTenantId = useCallback((id: string) => {
    setTenantIdState(id);
    try {
      localStorage.setItem(TENANT_KEY, id);
    } catch {
      // private mode: selection lasts for this page only
    }
  }, []);

  const value = useMemo<AuthState>(() => {
    const tenants = me?.tenants ?? [];
    const tenant = tenants.find((t) => t.id === tenantId) ?? tenants[0] ?? null;
    const user: User | undefined = me?.user;
    return {
      me, loading, tenant, setTenantId, reload,
      login: async (email, password) => setMe(await Api.login(email, password)),
      logout: async () => {
        await Api.logout().catch(() => undefined);
        setMe(null);
      },
      isAdmin: user?.role === 'admin',
      canManage: user?.role === 'admin' || user?.role === 'owner',
    };
  }, [me, loading, tenantId, setTenantId, reload]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
